import { Injectable, signal, WritableSignal } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";
import { NavigationBarItem } from "src/app/modules/bootstrap/bootstrap-components/navigation-bar/navigation-bar-item";
import { activatedRouteSnapshotGetPathFromRoot } from "src/app/support/activated-route";

const UNLOCK_TTL_MS: number = 10_000;

export interface PmsTabScope {
  name: string;
  scope: string;
  slug: string;
  category: string;
  defaultLock: boolean;
}

export interface PmsTabScopeCategory {
  name: string;
  icon: string;
  tabs: readonly PmsTabScope[];
}

const PMS_TAB_SCOPE_CATEGORY_ICONS: Readonly<Record<string, string>> = {
  Dashboard: "fa-light fa-house",
  Desk: "fa-light fa-desktop",
  Anagrafica: "fa-light fa-address-book",
  Degenza: "fa-light fa-hospitals",
  Preventivi: "fa-light fa-file-signature",
  Fatture: "fa-light fa-file-invoice-dollar",
  "Fatture elettroniche": "fa-light fa-file-code",
  "Tessera sanitaria": "fa-light fa-file-medical",
  Magazzino: "fa-light fa-warehouse",
  Inventario: "fa-light fa-boxes",
  Statistiche: "fa-light fa-chart-mixed",
  KPI: "fa-light fa-chart-line-up",
  Allarmi: "fa-light fa-siren-on",
  "Ricerche avanzate": "fa-light fa-filter",
  Comunicazioni: "fa-light fa-megaphone",
  Marketplace: "fa-light fa-puzzle",
  "Gestione utenti": "fa-light fa-users-cog",
  Configurazioni: "fa-light fa-cog",
  "Centro assistenza": "fa-light fa-circle-question"
};

export const PMS_TAB_SCOPES: readonly PmsTabScope[] = [
  {
    name: "Dashboard",
    scope: "organization/dashboard",
    slug: "organization-dashboard",
    category: "Dashboard",
    defaultLock: false
  },
  {
    name: "Agenda",
    scope: "organization/calendar",
    slug: "organization-calendar",
    category: "Desk",
    defaultLock: false
  },
  {
    name: "Flowboard",
    scope: "organization/board",
    slug: "organization-board",
    category: "Desk",
    defaultLock: false
  },
  {
    name: "Task",
    scope: "organization/tasks",
    slug: "organization-tasks",
    category: "Desk",
    defaultLock: false
  },
  {
    name: "Contatti",
    scope: "organization/customers-registry",
    slug: "organization-customers-registry",
    category: "Anagrafica",
    defaultLock: false
  },
  {
    name: "Pazienti",
    scope: "organization/patients-registry",
    slug: "organization-patients-registry",
    category: "Anagrafica",
    defaultLock: false
  },
  {
    name: "Ricoverati",
    scope: "organization/recoveries/in-progress",
    slug: "organization-recoveries-in-progress",
    category: "Degenza",
    defaultLock: false
  },
  {
    name: "Storico degenza",
    scope: "organization/recoveries/all",
    slug: "organization-recoveries-all",
    category: "Degenza",
    defaultLock: false
  },
  {
    name: "Preventivi",
    scope: "organization/quotes",
    slug: "organization-quotes",
    category: "Preventivi",
    defaultLock: false
  },
  {
    name: "Notule",
    scope: "organization/invoices/paper/draft",
    slug: "organization-invoices-paper-draft",
    category: "Fatture",
    defaultLock: false
  },
  {
    name: "Fatture emesse",
    scope: "organization/invoices/paper/emitted",
    slug: "organization-invoices-paper-emitted",
    category: "Fatture",
    defaultLock: false
  },
  {
    name: "Fatture elettroniche da inviare",
    scope: "organization/sendings/fatturazione-elettronica/invoices-to-send",
    slug: "organization-sendings-fatturazione-elettronica-invoices-to-send",
    category: "Fatture elettroniche",
    defaultLock: false
  },
  {
    name: "Invii fatturazione elettronica",
    scope: "organization/sendings/fatturazione-elettronica/sendings",
    slug: "organization-sendings-fatturazione-elettronica-sendings",
    category: "Fatture elettroniche",
    defaultLock: false
  },
  {
    name: "Fatture passive",
    scope: "organization/passive/passive",
    slug: "organization-passive-passive",
    category: "Fatture elettroniche",
    defaultLock: false
  },
  {
    name: "Tessera sanitaria invii",
    scope: "organization/tessera-sanitaria/sending",
    slug: "organization-tessera-sanitaria-sending",
    category: "Tessera sanitaria",
    defaultLock: false
  },
  {
    name: "Tessera sanitaria fatture da inviare",
    scope: "organization/sendings/tessera-sanitaria/invoices-to-send",
    slug: "organization-sendings-tessera-sanitaria-invoices-to-send",
    category: "Tessera sanitaria",
    defaultLock: false
  },
  {
    name: "Tessera sanitaria invii",
    scope: "organization/sendings/tessera-sanitaria/sendings",
    slug: "organization-sendings-tessera-sanitaria-sendings",
    category: "Tessera sanitaria",
    defaultLock: false
  },
  {
    name: "Consegne",
    scope: "warehouse/delivery",
    slug: "warehouse-delivery",
    category: "Magazzino",
    defaultLock: false
  },
  {
    name: "Vendite",
    scope: "warehouse/sellings",
    slug: "warehouse-sellings",
    category: "Magazzino",
    defaultLock: false
  },
  {
    name: "Inventario magazzino",
    scope: "warehouse/goods",
    slug: "warehouse-goods",
    category: "Magazzino",
    defaultLock: false
  },
  {
    name: "Storage",
    scope: "warehouse/storage",
    slug: "warehouse-storage",
    category: "Magazzino",
    defaultLock: false
  },
  {
    name: "Inventario",
    scope: "inventory",
    slug: "inventory",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Clienti insoluti",
    scope: "organization/late-paying-customers",
    slug: "organization-late-paying-customers",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Soddisfazione clienti",
    scope: "organization/surveys",
    slug: "organization-surveys",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Conti aperti",
    scope: "organization/open-books",
    slug: "organization-open-books",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Chiusura di cassa",
    scope: "organization/cash-closure",
    slug: "organization-cash-closure",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Tariffario",
    scope: "organization/rate-table",
    slug: "organization-rate-table",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Farmaci",
    scope: "organization/drugs",
    slug: "organization-drugs",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Prodotti",
    scope: "organization/products",
    slug: "organization-products",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Ordini di lavoro",
    scope: "organization/work-orders",
    slug: "organization-work-orders",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Ricette",
    scope: "organization/prescriptions",
    slug: "organization-prescriptions",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Esportazioni",
    scope: "organization/export",
    slug: "organization-export",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Importazioni",
    scope: "organization/import",
    slug: "organization-import",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Promemoria",
    scope: "organization/reminders",
    slug: "organization-reminders",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Rapporti di produzione",
    scope: "organization/remunerations",
    slug: "organization-remunerations",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Storage registry",
    scope: "warehouse/storage-registry",
    slug: "warehouse-storage-registry",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Unità organizzative",
    scope: "warehouse/business-unit-registry",
    slug: "warehouse-business-unit-registry",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Fornitori",
    scope: "warehouse/supplier-registry",
    slug: "warehouse-supplier-registry",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli preventivi",
    scope: "organization/quote-templates",
    slug: "organization-quote-templates",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli documenti",
    scope: "organization/documents",
    slug: "organization-documents",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli promemoria",
    scope: "organization/reminders-templates",
    slug: "organization-reminders-templates",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli schede",
    scope: "organization/soap-templates",
    slug: "organization-soap-templates",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli appuntamenti",
    scope: "organization/appointment-templates",
    slug: "organization-appointment-templates",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli vaccini",
    scope: "organization/vaccines",
    slug: "organization-vaccines",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Modelli comunicazioni",
    scope: "organization/campaign-templates",
    slug: "organization-campaign-templates",
    category: "Inventario",
    defaultLock: false
  },
  {
    name: "Statistiche fatturato",
    scope: "organization/analytics/sales",
    slug: "organization-analytics-sales",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "Statistiche incasso",
    scope: "organization/analytics/incomes",
    slug: "organization-analytics-incomes",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "Statistiche contatti",
    scope: "organization/analytics/customers",
    slug: "organization-analytics-customers",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "Statistiche pazienti",
    scope: "organization/analytics/patients",
    slug: "organization-analytics-patients",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "Statistiche check-in",
    scope: "organization/analytics/tickets",
    slug: "organization-analytics-tickets",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "Statistiche promemoria",
    scope: "organization/analytics/reminders",
    slug: "organization-analytics-reminders",
    category: "Statistiche",
    defaultLock: false
  },
  {
    name: "KPI",
    scope: "organization/kpi",
    slug: "organization-kpi",
    category: "KPI",
    defaultLock: false
  },
  {
    name: "Allarmi",
    scope: "organization/alarms",
    slug: "organization-alarms",
    category: "Allarmi",
    defaultLock: false
  },
  {
    name: "Ricerche avanzate",
    scope: "organization/filters",
    slug: "organization-filters",
    category: "Ricerche avanzate",
    defaultLock: false
  },
  {
    name: "Campagne",
    scope: "organization/comunications/campaigns",
    slug: "organization-comunications-campaigns",
    category: "Comunicazioni",
    defaultLock: false
  },
  {
    name: "Liste",
    scope: "organization/comunications/segmentations",
    slug: "organization-comunications-segmentations",
    category: "Comunicazioni",
    defaultLock: false
  },
  {
    name: "Marketplace",
    scope: "organization/integrations",
    slug: "organization-integrations",
    category: "Marketplace",
    defaultLock: false
  },
  {
    name: "Utenti",
    scope: "configuration/manage-users/members",
    slug: "configuration-manage-users-members",
    category: "Gestione utenti",
    defaultLock: false
  },
  {
    name: "Gruppi",
    scope: "configuration/manage-users/groups",
    slug: "configuration-manage-users-groups",
    category: "Gestione utenti",
    defaultLock: false
  },
  {
    name: "Ruoli",
    scope: "configuration/manage-users/roles",
    slug: "configuration-manage-users-roles",
    category: "Gestione utenti",
    defaultLock: false
  },
  {
    name: "Turni",
    scope: "configuration/manage-users/shifts",
    slug: "configuration-manage-users-shifts",
    category: "Gestione utenti",
    defaultLock: false
  },
  {
    name: "Medici esterni",
    scope: "organization/physicians",
    slug: "organization-physicians",
    category: "Gestione utenti",
    defaultLock: false
  },
  {
    name: "Stanze",
    scope: "company/wards",
    slug: "company-wards",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Configurazioni",
    scope: "company",
    slug: "company",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Profili fiscali",
    scope: "organization/fiscal-profiles",
    slug: "organization-fiscal-profiles",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Tag",
    scope: "configuration/tag",
    slug: "configuration-tag",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Carta intestata",
    scope: "configuration/letterhead",
    slug: "configuration-letterhead",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Conti corrente",
    scope: "organization/bank-accounts",
    slug: "organization-bank-accounts",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Metodi di pagamento",
    scope: "organization/payment-methods",
    slug: "organization-payment-methods",
    category: "Configurazioni",
    defaultLock: false
  },
  {
    name: "Centro assistenza",
    scope: "organization/help-center",
    slug: "organization-help-center",
    category: "Centro assistenza",
    defaultLock: false
  }
];

export function groupPmsTabScopesByCategory(
  scopes: readonly PmsTabScope[]
): PmsTabScopeCategory[] {
  const tabsByCategory = new Map<string, PmsTabScope[]>();
  const categoryOrder: string[] = [];

  for (const tab of scopes) {
    if (!tabsByCategory.has(tab.category)) {
      tabsByCategory.set(tab.category, []);
      categoryOrder.push(tab.category);
    }

    tabsByCategory.get(tab.category)!.push(tab);
  }

  return categoryOrder.map((name: string) => ({
    name,
    icon: PMS_TAB_SCOPE_CATEGORY_ICONS[name] ?? "fa-light fa-folder",
    tabs: tabsByCategory.get(name)!
  }));
}

export function getPmsTabScopeByScope(scope: string): PmsTabScope | undefined {
  return PMS_TAB_SCOPES.find((tab: PmsTabScope) => tab.scope === scope);
}

export function isScopeProtected(
  scope: string,
  protectedRoutes: string[] | undefined
): boolean {
  const tab = PMS_TAB_SCOPES.find((t) => t.scope === scope);

  if (protectedRoutes !== undefined) {
    if (!tab) {
      return false;
    }

    return protectedRoutes.includes(tab.slug);
  }

  return tab?.defaultLock ?? false;
}

@Injectable()
export class ProtectedTabService {
  #protectedRoutes: string[] | undefined;
  readonly protectedRoutesRevision: WritableSignal<number> = signal<number>(0);
  private readonly unlockedScopes = new Map<string, number>();

  setProtectedRoutes(protectedRoutes: string[] | undefined): void {
    this.#protectedRoutes = protectedRoutes;
    this.protectedRoutesRevision.update((revision: number) => revision + 1);
  }

  getProtectedRoutes(): string[] | undefined {
    return this.#protectedRoutes;
  }

  isNavigationBarItemProtected(item: NavigationBarItem): boolean {
    if (!this.#protectedRoutes?.length || item.header) {
      return false;
    }

    if (item.url) {
      const scope: string | undefined = this.getScopeForUrl(item.url);

      if (scope && this.isScopeLocked(scope)) {
        return true;
      }
    }

    const actionableChildren: NavigationBarItem[] =
      item.items?.filter(
        (child: NavigationBarItem) => !child.header && !!child.url
      ) ?? [];

    if (actionableChildren.length === 1) {
      const childScope: string | undefined = this.getScopeForUrl(
        actionableChildren[0].url
      );

      return childScope ? this.isScopeLocked(childScope) : false;
    }

    return false;
  }

  getScopeForUrl(url: string | undefined): string | undefined {
    if (!url || url === "" || url === "not-found") {
      return undefined;
    }

    const normalizedUrl: string = this.normalizePath(
      url.startsWith("/") ? url : `/${url}`
    );
    let bestScope: string | undefined;
    let bestScopeLength: number = 0;

    for (const tab of PMS_TAB_SCOPES) {
      if (
        normalizedUrl.includes(`/${tab.scope}`) &&
        tab.scope.length > bestScopeLength
      ) {
        bestScopeLength = tab.scope.length;
        bestScope = tab.scope;
      }
    }

    return bestScope;
  }

  getProtectionScope(route: ActivatedRouteSnapshot): string | undefined {
    const normalizedUrl: string = this.normalizePath(
      activatedRouteSnapshotGetPathFromRoot(route)
    );
    let bestScope: string | undefined;
    let bestScopeLength: number = 0;

    for (const tab of PMS_TAB_SCOPES) {
      if (
        normalizedUrl.includes(`/${tab.scope}`) &&
        tab.scope.length > bestScopeLength
      ) {
        bestScopeLength = tab.scope.length;
        bestScope = tab.scope;
      }
    }

    return bestScope;
  }

  getProtectedTabForRoute(
    route: ActivatedRouteSnapshot
  ): PmsTabScope | undefined {
    const scope: string | undefined = this.getProtectionScope(route);

    return scope ? getPmsTabScopeByScope(scope) : undefined;
  }

  isTabProtected(route: ActivatedRouteSnapshot): boolean {
    const scope: string | undefined = this.getProtectionScope(route);

    if (!scope) {
      return false;
    }

    if (!this.isScopeLocked(scope)) {
      return false;
    }

    const expiresAt: number | undefined = this.unlockedScopes.get(scope);

    if (!expiresAt) {
      return true;
    }

    if (Date.now() >= expiresAt) {
      this.unlockedScopes.delete(scope);

      return true;
    }

    return false;
  }

  unlockTab(route: ActivatedRouteSnapshot): void {
    const scope: string | undefined = this.getProtectionScope(route);

    if (scope) {
      this.unlockedScopes.set(scope, Date.now() + UNLOCK_TTL_MS);
    }
  }

  private isScopeLocked(scope: string): boolean {
    return isScopeProtected(scope, this.#protectedRoutes);
  }

  private normalizePath(url: string): string {
    const segments: string[] = url.split("/").filter(Boolean);

    if (segments.length <= 2) {
      return `/${segments.join("/")}`;
    }

    return `/${segments.slice(2).join("/")}`;
  }
}
